import {
  getSortedRowModel,
  type Row,
  type RowData,
  type RowModel,
  type SortingState,
  type Table,
} from '@tanstack/react-table';

export function sortingStateKey(sorting: SortingState): string {
  return JSON.stringify(sorting);
}

export function sameRowIdSet(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  const ids = new Set(left);
  for (const id of right) {
    if (!ids.has(id)) return false;
  }
  return true;
}

/** True when we should re-sort live (header click, first sort, or a different set of rows). */
export function shouldRefreshFrozenSort(
  sortingKey: string,
  lastSortingKey: string,
  frozenIds: string[] | null,
  currentIds: string[]
): boolean {
  if (!frozenIds) return true;
  if (sortingKey !== lastSortingKey) return true;
  return !sameRowIdSet(frozenIds, currentIds);
}

export function applyFrozenRowOrder<T extends { id: string }>(
  rows: T[],
  frozenIds: string[]
): T[] {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const ordered: T[] = [];
  for (const id of frozenIds) {
    const row = byId.get(id);
    if (row) {
      ordered.push(row);
      byId.delete(id);
    }
  }
  for (const row of rows) {
    if (byId.has(row.id)) ordered.push(row);
  }
  return ordered;
}

function flattenRows<TData extends RowData>(rows: Row<TData>[]): Row<TData>[] {
  const flat: Row<TData>[] = [];
  for (const row of rows) {
    flat.push(row);
    if (row.subRows?.length) {
      flat.push(...flattenRows(row.subRows));
    }
  }
  return flat;
}

/**
 * Like getSortedRowModel, but keeps the last header-click order while cell
 * values change (in-place edits). Re-sorts when the user clicks a header
 * again, or when the set of row IDs changes.
 */
export function getFrozenSortedRowModel<TData extends RowData>(): (
  table: Table<TData>
) => () => RowModel<TData> {
  return (table) => {
    const getLiveSorted = getSortedRowModel<TData>()(table);
    let lastSortingKey = '';
    let frozenIds: string[] | null = null;

    return () => {
      const sorting = table.getState().sorting;
      const preSorted = table.getPreSortedRowModel();
      const sortingKey = sortingStateKey(sorting ?? []);

      if (!preSorted.rows.length || !sorting?.length) {
        lastSortingKey = sortingKey;
        frozenIds = null;
        return preSorted;
      }

      const currentIds = preSorted.rows.map((row) => row.id);
      if (shouldRefreshFrozenSort(sortingKey, lastSortingKey, frozenIds, currentIds)) {
        lastSortingKey = sortingKey;
        const sorted = getLiveSorted();
        frozenIds = sorted.rows.map((row) => row.id);
        return sorted;
      }

      const rows = applyFrozenRowOrder(preSorted.rows, frozenIds ?? currentIds);
      return {
        rows,
        flatRows: flattenRows(rows),
        rowsById: preSorted.rowsById,
      };
    };
  };
}
